import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert,
  LinearProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import {
  MenuBook as ReflectionsIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  LightbulbOutlined as InsightIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format, subDays, parseISO } from 'date-fns';
import { reflectionsApi } from '../api/services';

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  bgPaper: '#121829',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
  gridLine: '#1E293B',
};

const TRIGGER_LABELS: Record<string, { label: string; color: string }> = {
  CloseDay: { label: 'Close Day', color: COLORS.primary },
  MiddayReset: { label: 'Midday Reset', color: COLORS.warning },
  WeeklyReset: { label: 'Weekly Reset', color: COLORS.secondary },
  Manual: { label: 'Manual', color: COLORS.success },
  PlanTomorrow: { label: 'Plan Tomorrow', color: '#CE93D8' },
};

interface Reflection {
  id: string;
  date: string;
  trigger: string;
  content: string;
  insights?: string;
  adjustmentsMade?: string;
  createdAt: string;
}

export default function ReflectionsPage() {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [todayReflections, setTodayReflections] = useState<Reflection[]>([]);
  const [pastReflections, setPastReflections] = useState<Reflection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add Reflection dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newReflection, setNewReflection] = useState({
    trigger: 'Manual',
    content: '',
    insights: '',
    adjustmentsMade: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const loadReflections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load selected date reflections
      let todayData: Reflection[] = [];
      try {
        const result = await reflectionsApi.getByDate(selectedDate);
        todayData = Array.isArray(result) ? result : result ? [result] : [];
      } catch (err: any) {
        if (err.response?.status !== 404) throw err;
      }
      setTodayReflections(todayData);

      // Load past 7 days (excluding selected date)
      const pastResults: Reflection[] = [];
      for (let i = 1; i <= 7; i++) {
        const pastDate = format(subDays(parseISO(selectedDate), i), 'yyyy-MM-dd');
        try {
          const result = await reflectionsApi.getByDate(pastDate);
          const items = Array.isArray(result) ? result : result ? [result] : [];
          pastResults.push(...items);
        } catch {
          // 404 is expected for days without reflections
        }
      }
      setPastReflections(pastResults);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load reflections');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { loadReflections(); }, [loadReflections]);

  const handleCreateReflection = async () => {
    if (!newReflection.content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await reflectionsApi.create({
        date: selectedDate,
        trigger: newReflection.trigger,
        content: newReflection.content,
        insights: newReflection.insights || undefined,
        adjustmentsMade: newReflection.adjustmentsMade || undefined,
      });
      setDialogOpen(false);
      setNewReflection({ trigger: 'Manual', content: '', insights: '', adjustmentsMade: '' });
      setSaveSuccess('Reflection saved successfully.');
      loadReflections();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save reflection');
    } finally {
      setSaving(false);
    }
  };

  const getTriggerInfo = (trigger: string) => TRIGGER_LABELS[trigger] || { label: trigger, color: COLORS.textSecondary };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReflectionsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h4">Reflections</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Daily journal and insights
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Reflection
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(null)}>{saveSuccess}</Alert>}

      {/* Today's Reflections */}
      <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
        {selectedDate === format(new Date(), 'yyyy-MM-dd') ? "Today's Reflections" : `Reflections for ${format(parseISO(selectedDate), 'dd MMM yyyy')}`}
      </Typography>

      {!loading && todayReflections.length === 0 && (
        <Card sx={{ bgcolor: COLORS.bgPaper, mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ReflectionsIcon sx={{ fontSize: 48, color: COLORS.textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No reflections yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Reflections are captured when you close your day, or you can add one manually.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add Reflection
            </Button>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2} sx={{ mb: 4 }}>
        {todayReflections.map((reflection) => (
          <ReflectionCard key={reflection.id} reflection={reflection} getTriggerInfo={getTriggerInfo} />
        ))}
      </Stack>

      {/* Past Reflections */}
      {pastReflections.length > 0 && (
        <>
          <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2, mt: 3 }}>
            Past 7 Days
          </Typography>
          <Stack spacing={2}>
            {pastReflections.map((reflection) => (
              <ReflectionCard key={reflection.id} reflection={reflection} getTriggerInfo={getTriggerInfo} />
            ))}
          </Stack>
        </>
      )}

      {/* Add Reflection Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Reflection</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Trigger</InputLabel>
              <Select
                value={newReflection.trigger}
                label="Trigger"
                onChange={e => setNewReflection(prev => ({ ...prev, trigger: e.target.value }))}
              >
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="CloseDay">Close Day</MenuItem>
                <MenuItem value="MiddayReset">Midday Reset</MenuItem>
                <MenuItem value="WeeklyReset">Weekly Reset</MenuItem>
                <MenuItem value="PlanTomorrow">Plan Tomorrow</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Content"
              multiline
              rows={4}
              fullWidth
              value={newReflection.content}
              onChange={e => setNewReflection(prev => ({ ...prev, content: e.target.value }))}
              placeholder="What happened today? What went well? What could improve?"
            />
            <TextField
              label="Insights"
              multiline
              rows={2}
              fullWidth
              value={newReflection.insights}
              onChange={e => setNewReflection(prev => ({ ...prev, insights: e.target.value }))}
              placeholder="Key insights or patterns noticed..."
            />
            <TextField
              label="Adjustments Made"
              multiline
              rows={2}
              fullWidth
              value={newReflection.adjustmentsMade}
              onChange={e => setNewReflection(prev => ({ ...prev, adjustmentsMade: e.target.value }))}
              placeholder="Changes or decisions made as a result..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateReflection}
            disabled={saving || !newReflection.content.trim()}
          >
            {saving ? 'Saving...' : 'Save Reflection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ReflectionCard({
  reflection,
  getTriggerInfo,
}: {
  reflection: Reflection;
  getTriggerInfo: (trigger: string) => { label: string; color: string };
}) {
  const triggerInfo = getTriggerInfo(reflection.trigger);
  let displayDate: string;
  try {
    displayDate = format(parseISO(reflection.date), 'EEEE, dd MMM yyyy');
  } catch {
    displayDate = reflection.date;
  }

  return (
    <Card sx={{ bgcolor: COLORS.bgPaper, borderLeft: 3, borderColor: triggerInfo.color }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarIcon sx={{ fontSize: 16, color: COLORS.textSecondary }} />
            <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
              {displayDate}
            </Typography>
            <Chip
              label={triggerInfo.label}
              size="small"
              sx={{
                bgcolor: `${triggerInfo.color}20`,
                color: triggerInfo.color,
                fontWeight: 600,
                height: 22,
              }}
            />
          </Stack>
          {reflection.createdAt && (
            <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
              {format(parseISO(reflection.createdAt), 'HH:mm')}
            </Typography>
          )}
        </Box>

        <Typography variant="body2" sx={{ color: COLORS.textPrimary, whiteSpace: 'pre-wrap', mb: 1.5 }}>
          {reflection.content}
        </Typography>

        {reflection.insights && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(108, 155, 255, 0.06)', border: '1px solid rgba(108, 155, 255, 0.12)' }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
              <InsightIcon sx={{ fontSize: 16, color: COLORS.primary }} />
              <Typography variant="caption" sx={{ color: COLORS.primary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Insights
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: COLORS.textPrimary, whiteSpace: 'pre-wrap' }}>
              {reflection.insights}
            </Typography>
          </Box>
        )}

        {reflection.adjustmentsMade && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(105, 240, 174, 0.06)', border: '1px solid rgba(105, 240, 174, 0.12)' }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
              <EditIcon sx={{ fontSize: 16, color: COLORS.success }} />
              <Typography variant="caption" sx={{ color: COLORS.success, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Adjustments Made
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: COLORS.textPrimary, whiteSpace: 'pre-wrap' }}>
              {reflection.adjustmentsMade}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
